

import { Card, CardActionArea, Box, Typography, LinearProgress, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useCommonStyles } from '../../styles/GenericStyle';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useActiveSplots } from '../../api/vegApl/Technician';
import { useEffect, useState } from 'react';
import FullScreenActionLoader from '../common/FullScreenActionLoader';
import { useGlobalState } from "../../contexts/GlobalState";
import { ProcessingWorkflow } from '../../pages/ProcessingWorkflow';

type TaskStepCardProps = {
    type: string;
    setCardType: (data: any) => void;
    crop: string | null,
    pageTitle: string
};

export default function TaskStepCard({
    setCardType,
    crop,
    type,
    pageTitle
}: TaskStepCardProps) {
    const classes = useCommonStyles();
    const { showSnackbar } = useSnackbar();
    const { state: globalState, state: { mobileHome } } = useGlobalState();
    const [SPLotData, setSPLotData] = useState<any[]>([]);
    const [selSPLotId, setSPLotId] = useState<any[]>([]);
    const [isLoadingSPLots, setIsLoadingSPLots] = useState(false);

    // Reset drill-down view when user taps the home icon
    useEffect(() => {
        if (mobileHome) {
            setSPLotId([]);
        }
    }, [mobileHome]);
    const cardClick = (item: any) => {
        setCardType((prev: any) => ({ ...prev, isShow: !prev.isShow, type: item }));
    }

    const { mutate: getActiveSplots } = useActiveSplots({
        mutation: {
            onSuccess: (data: any) => {
                setIsLoadingSPLots(false);
                let activeSPLotData = data?.data;
                console.log("activeSPLotData", activeSPLotData);
                const filtered = activeSPLotData
                    .filter((item: any) => item?.current_workflow_step_status.toLowerCase() === type.toLowerCase())
                    .map((item: any) => item);
                setSPLotData(filtered);
            },

            onError: (error: any) => {
                setIsLoadingSPLots(false);
                let errorMessage = (error?.response?.data?.detail) || "Error";
                showSnackbar({ message: errorMessage, severity: 'error' });
            }
        },
    });

    useEffect(() => {
        console.log("Global allocated sites in TechnicianStatus: ", globalState?.allocatedSites?.[0]);
        setIsLoadingSPLots(true);
        getActiveSplots({ site_code: globalState.allocatedSites?.[0]?.full_code, crop_code: crop ?? "" });
    }, [globalState.allocatedSites, crop, type])


    const getProgress = (cur: number, total: number) =>
        Math.min(100, Math.max(0, (cur / total) * 100));

    const processingGreen = '#07A807';
    return (
        <>
            <FullScreenActionLoader open={isLoadingSPLots} />
            {!selSPLotId.length && <Box width="100%">
                <Box display="flex" width="100%" alignItems="center" justifyContent="flex-start">
                    <ArrowBackIosIcon
                        sx={{
                            verticalAlign: 'middle',
                            cursor: 'pointer',
                            fontSize: 28,
                            marginLeft: '20px'
                        }}
                        onClick={() => cardClick("")}
                    />
                    <Typography component="div" sx={{ py: 1.25, px: 1.5 }} className={classes.labelNoto20Bold500_hh}>
                        {pageTitle} SP Lots ({SPLotData?.length})
                    </Typography>
                </Box>
                <Divider
                    sx={{
                        borderColor: 'grey.400',
                        borderBottomWidth: '1px',
                        position: 'relative',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        width: '100%',
                    }} />

                {/* Cards */}

                {SPLotData?.map((item) => (
                    <Box sx={{ px: 2 }}>
                        <Card
                            key={item?.title}
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                border: '1px solid #D4D4D4',
                                mt: 1.5,
                            }}>
                            <CardActionArea sx={{ p: 1 }} onClick={() => setSPLotId([item?.splot_id])}>
                                <Box display="flex" sx={{ width: '100%', p: 1}} gap={1} flexDirection="row" >
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "8px",
                                            padding:"8px",
                                            bgcolor: pageTitle=="Idle" ? "#E17100"  : processingGreen,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            boxShadow: `0 0 0 1px ${alpha('#000', 0.03)} inset`,
                                        }}>
                                        <img
                                            src={"icons/SP_Lots_Icon.svg"}
                                            alt="SP Lot"
                                            width={28.8}
                                            height={28.8}
                                            style={{ display: "block" }}
                                        />
                                    </Box>

                                    <Box display="flex" sx={{ width: '100%',mt:0.5 }} flexDirection="column">
                                        <Typography
                                            component="span"
                                            title={item?.splot_id}
                                            className={classes.labelNoto16Bold400_hh}
                                            sx={{
                                                lineHeight: 1.2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}>
                                            {item?.splot_id}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            className={classes.labelNoto12Bold400_hh}
                                            sx={{ display: 'block', mt: 0.25 }}
                                        >
                                            {item?.completed_workflow_steps}/{item?.total_workflow_steps} steps
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            <Box
                                                component="span"
                                                sx={{ color: 'text.primary' }}
                                                className={classes.labelNoto12Bold400_hh}>
                                                Current : {item?.current_workflow_step_name}
                                            </Box>
                                        </Typography>

                                        <LinearProgress
                                            variant="determinate"
                                            value={getProgress(item?.completed_workflow_steps, item?.total_workflow_steps)}
                                            sx={{
                                                mt: 1,
                                                height: 8,
                                                borderRadius: 999,
                                                width: '100%',
                                                backgroundColor: '#E5E7EB',
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: pageTitle=="Idle" ? "#E17100"  : processingGreen,
                                                    borderRadius: 999,
                                                },
                                            }}
                                        />
                                    </Box>
                                    <ChevronRightRoundedIcon sx={{ color: 'text.secondary', ml: 'auto' }} aria-hidden />
                                </Box>
                            </CardActionArea>
                        </Card>
                    </Box>
                ))}
            </Box>}

            {selSPLotId?.length == 1 && (
                <ProcessingWorkflow
                    spLotIds={selSPLotId}
                    originComponent={pageTitle === 'Idle' ? 'idle-card' : 'processing-card'}
                    onBack={() => setSPLotId([])}
                />
            )}

        </>
    );
}
